<template>
  <div class="page-table scrollable">
    <el-table
      :data="collection"
      :height="height || tableHeight"
      :row-style="{ 'max-height': '40px' }"
      ref="cTable"
      highlight-current-row
      @current-change="handleCurrentChange"
      @sort-change="sortChange"
      style="width: 100%"
    >
      <el-table-column
        v-for="(col, idx) in columns"
        :key="idx"
        :type="col.datatype"
        :prop="col.name"
        :label="col.label ? col.label : col.name"
        :width="col.width"
        :min-width="col['min-width']"
        :row-key="currentRow"
        :sortable="col.sortable ? 'custom' : false"
        class-name="subtitle2"
      >
        <template slot-scope="scope">
          <div v-if="col.datatype === 'boolean'">
            <el-tag size="small">{{ scope.row[col.name] ? 'Active' : 'Inactive' }}</el-tag>
          </div>

          <div v-else-if="col.datatype === 'datetime'">
            <time class="body1">{{ getFormatedDateTime(scope.row, col) }}</time>
          </div>

          <div v-else-if="col.datatype === 'objectpath' && !col.display">
            <span class="body1">{{ getObjectPathValue(scope.row, col.name) }}</span>
          </div>

          <div v-else-if="col.display === 'icon'">
            <span class="body1"> <i :class="getIcon(scope.row, col, col.displayKey).icon"></i> </span>
          </div>

          <div v-else-if="col.display === 'iconAndText'">
            <span class="body1">
              <i
                class="display-inline-block"
                :style="[
                  { color: getColorTagValue(scope.row, col, col.displayKey).color },
                  { background: getColorTagValue(scope.row, col, col.displayKey).color }
                ]"
                :class="getIcon(scope.row, col, col.displayKey).icon"
              ></i>
              {{ getColorTagValue(scope.row, col, col.displayKey).display }}
            </span>
          </div>

          <div v-else-if="col.display === 'colorTag'">
            <el-tag
              class="button2 capitalize-first-letter"
              size="small"
              :style="[
                { color: getColorTagValue(scope.row, col, col.displayKey).textColor },
                { 'border-color': getColorTagValue(scope.row, col, col.displayKey).textColor }
              ]"
              :color="getColorTagValue(scope.row, col, col.displayKey).color"
              >{{ getColorTagValue(scope.row, col, col.displayKey).display }}</el-tag
            >
          </div>

          <div v-else-if="col.display === 'stateTag'">
            <state-tag :status="scope.row[col.name]" :class-list="'button2 capitalize-first-letter'"/>
          </div>

          <div v-else-if="col.display === 'link'">
            <router-link v-if="col.query" :to="{ name: col.pathTo, query: getQueryParamObject(scope.row, col) }">
              <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
              <a v-else>
                {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
              </a>
            </router-link>
            <router-link v-else :to="{ name: col.pathTo, params: getQueryParamObject(scope.row, col, 'params') }">
              <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
              <a v-else>
                {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
              </a>
            </router-link>
          </div>

          <div v-else-if="col.display === 'mitrelink'">
            <span class="value">
              <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer"
                >{{ getObjectPathValue(scope.row, col.linkLabel.labelId) }}:
                {{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a
              >
            </span>
          </div>

          <div v-else-if="col.display === 'popup'">
            <el-link @click="emitCallbackEvent('popup-click', scope.row, col.name)">{{
              col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name]
            }}</el-link>
          </div>

          <div v-else-if="col.display === 'riskscore'">
            <span class="body1">
              <i class="color-block" :class="`${scope.row.riskLevel.level}-risk-color`"></i>
              <span class="body1">{{ scope.row[col.name] }}</span>
            </span>
          </div>

          <div v-else-if="col.display === 'switch'">
            <span class="body1">
              <el-switch
                v-model="scope.row[col.name]"
                :width="40"
                :active-value="col.displayKey.active.key"
                :inactive-value="col.displayKey.inactive.key"
                :active-text="
                  getObjectPathValue(scope.row, col.name) === col.displayKey.active.key
                    ? col.displayKey.active.display
                    : col.displayKey.inactive.display
                "
                :disabled="true"
              >
              </el-switch>
            </span>
          </div>

          <div v-else-if="col.display === 'toggle'">
            <span class="body1">
              <el-switch
                v-model="scope.row[col.name]"
                :width="40"
                :active-text="scope.row[col.name] ? 'Active' : 'Paused'"
              >
              </el-switch>
            </span>
          </div>

          <div v-else-if="col.display === 'component'">
            <span v-if="col.component === 'CyberKillChainTickChart'">
              <cyber-kill-chain-tick-chart
                :chart-id="`${scope.$index}`"
                :chart-data="getObjectPathValue(scope.row, col.name)"
                :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
              />
              <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer" v-if="col.link">
                <span v-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
              </a>
              <span v-else-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
            </span>
            <span v-if="col.component === 'RecommendationProgress'">
              <recommendation-progress :row-data="scope.row" />
            </span>
            <span v-if="col.component === 'PercentComplete'">
              <percent-complete
                :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                :total="getObjectPathValue(scope.row, col.componentFields.total)"
              />
            </span>
            <span v-if="col.component === 'UserButton'">
              <user-button
                :classList="getObjectPathValue(scope.row, col.name).firstname ? 'shamrock-200' : ''"
                :user="getObjectPathValue(scope.row, col.name)"
                :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
              />
              {{ getObjectPathValue(scope.row, col.name).fullname }}
            </span>
            <span v-if="col.component === 'IntegrationStatusTableCell'">
              <integration-status-table-cell
                :status="getObjectPathValue(scope.row, col.componentFields.status)"
                :assetCount="getObjectPathValue(scope.row, col.componentFields.assetCount)"
                :assetText="getObjectPathValue(scope.row, col.componentFields.assetText)"
                :lastScanTime="getObjectPathValue(scope.row, col.componentFields.lastScanTime)"
                :assetLink="col.componentFields.assetLink"
                :accountType="getObjectPathValue(scope.row, col.componentFields.accountType)"
                :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
              />
            </span>
            <span v-if="col.component === 'AssetCountTableCell'">
              <asset-count-table-cell
                :vmCount="getObjectPathValue(scope.row, col.componentFields.vmCount)"
                :containerCount="getObjectPathValue(scope.row, col.componentFields.containerCount)"
                :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
              />
            </span>
            <span v-if="col.component === 'SensorCoverageTableCell'">
              <sensor-coverage-table-cell
                :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                :total="getObjectPathValue(scope.row, col.componentFields.total)"
              />
            </span>
            <span v-if="col.component === 'DiscoverySourceTableCell'">
              <discovery-source-table-cell
                :icon="getIcon(scope.row, col, col.displayKey).icon"
                :discoveryKey="getObjectPathValue(scope.row, col.componentFields.discoveryKey)"
              />
            </span>
            <span v-else-if="col.component === 'InstanceEntityTableCell'">
              <instance-entity-table-cell
                :entities="getObjectPathValue(scope.row, col.componentFields.entities)"
              ></instance-entity-table-cell>
            </span>
            <span v-else-if="col.component === 'AnomalyDismissTableCell'">
              <anomaly-dismiss-table-cell
                :entities="getObjectPathValue(scope.row, col.componentFields.entities)"
                :state="getObjectPathValue(scope.row, col.componentFields.state)"
                :uid="getObjectPathValue(scope.row, col.componentFields.uid)"
              ></anomaly-dismiss-table-cell>
            </span>
          </div>

          <div v-else-if="col.display === 'tag'">
            <el-tag size="medium" v-for="(item, idx) in scope.row[col.name]" :key="idx">{{ item }}</el-tag>
          </div>

          <span v-else-if="col.display === 'fingerprintLabel'" class="body1">
            {{ scope.row[col.display] }}
            <span
              v-if="scope.row[col.name]"
              class="display-inline-block icon-copy-to-clipboard cursor-pointer"
              @click="copyToClipboard(scope.row, col.name)"
            >
            </span>
          </span>

          <div v-else-if="col.display === 'multicolumn'">
            <span v-for="(eaCol, idx) in col.columns" :key="idx" class="display-block">
              <span class="display-inline-block"> {{ eaCol.label }}: </span>
              <span v-if="col.datatype === 'datetime'">
                <time class="display-inline-block body1">{{ getFormatedDateTime(scope.row, col) }}</time>
              </span>
              <span v-else class="display-inline-block"> {{ getObjectPathValue(scope.row, eaCol.name) }} </span>
            </span>
          </div>

          <div v-else-if="col.datatype === 'array' || col.display === 'array'">
            <span v-for="(item, idx) in getObjectPathValue(scope.row, col.name)" :key="idx">
              {{ item }}
              <el-divider
                v-if="idx !== getObjectPathValue(scope.row, col.name).length - 1"
                direction="vertical"
              ></el-divider>
            </span>
          </div>

          <div v-else>
            <span class="body1">{{ scope.row[col.name] }}</span>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      background
      layout="prev, pager, next"
      :page-size="this.tableData.recordsPerPage"
      :total="this.tableData.totalRecords"
      :current-page="this.tableData.currentPage"
      prev-text="PREV"
      next-text="NEXT"
      hide-on-single-page
      style="text-align: center; padding-top: 20px"
      @current-change="changePage"
    ></el-pagination>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { NANOSECONDS_TO_MILLISECONDS } from '@/constants/datetime.constants'
import { OrderByEnum } from '@/constants/orderby.constants'
import RecommendationProgress from '@/components/chart/RecommendationProgress.vue'
import PercentComplete from '@/components/chart/PercentComplete.vue'
import CyberKillChainTickChart from '@/components/chart/CyberKillChainTickChart.vue'
import UserButton from '@/components/button/UserButton.vue'
import IntegrationStatusTableCell from '@/components/table/cell/IntegrationStatusTableCell.vue'
import AssetCountTableCell from '@/components/table/cell/AssetCountTableCell.vue'
import SensorCoverageTableCell from '@/components/table/cell/SensorCoverageTableCell.vue'
import DiscoverySourceTableCell from '@/components/table/cell/DiscoverySourceTableCell.vue'
import InstanceEntityTableCell from '@/components/table/cell/InstanceEntityTableCell.vue'
import AnomalyDismissTableCell from '@/components/table/cell/AnomalyDismissTableCell.vue'
import NeedsAttentionTableCell from '@/components/table/cell/NeedsAttentionTableCell.vue'
import SnykVulnerableTableCell from '@/components/table/cell/SnykVulnerableTableCell.vue'
import ContainerImageNeedsAttentionTableCell from '@/components/table/cell/ContainerImageNeedsAttentionTableCell.vue'
import StateTag from "../button/StateTag.vue";

export default {
  name: 'BasicTableElement',
  props: {
    height: { default: 0, type: Number },
    heightOffset: { default: 0, type: Number },
    columns: { type: Array },
    tableData: { type: Object }
  },
  data () {
    return {
      currentRow: undefined,
      collection: [],
      // page: 1,
      tableHeight: 'auto'
    }
  },
  methods: {
    emitCallbackEvent (emitEvent, row, col) {
      this.$emit(emitEvent, { row: row, col: col })
    },
    getQueryParamObject (obj, col, queryParamString = 'query') {
      let queryParamName = col[queryParamString]
      if (_.isArray(col[queryParamString])) {
        let queryParam = {}
        for (let routeParam of col[queryParamString]) {
          Object.assign(queryParam, {
            [routeParam.name]: this.getObjectPathValue(obj, routeParam.keyLookup)
          })
        }
        return queryParam
      }
      return {
        [queryParamName.name]: this.getObjectPathValue(obj, queryParamName.keyLookup)
      }
    },
    getObjectPathValue (obj, path) {
      return _.get(obj, path)
    },
    getFormatedDateTime (row, col) {
      let timeVal = this.getObjectPathValue(row, col.name)

      if (timeVal === 'NA' || timeVal === 'N/A') {
        return timeVal
      }

      if (!_.isNumber(timeVal) && moment(timeVal, moment.ISO_8601, true).isValid()) {
        timeVal = new Date(timeVal).getTime()
      } else {
        timeVal = parseInt(timeVal)
      }

      // convert startTime to ms if necessary
      if (timeVal && !moment(timeVal).isValid()) {
        timeVal = timeVal * NANOSECONDS_TO_MILLISECONDS
      }
      return moment(timeVal).format(col.format)
    },
    getIcon (row, col, iconList) {
      let val = col.datatype === 'objectpath' ? _.get(row, col.name) : row[col.name]
      // hanlde case when no value is returned
      // ioc-detections on hosts not returned in hostlist (decommissioned?)
      if (_.isUndefined(val)) {
        return ''
      }
      return _.find(iconList, { key: val })
    },
    getColorTagValue (row, col, tagList) {
      let val = col.datatype === 'objectpath' ? _.get(row, col.name) : row[col.name]

      if (['agentDetails.openToPublicInternet', 'openToPublicInternet'].includes(col.name) && _.isUndefined(val)) {
        val = 'n/a'
      }

      return _.find(tagList, { key: val })
    },
    handleCurrentChange (val) {
      this.currentRow = val
    },
    headerSortChange (column, elm) {
      if (!elm) {
        return false
      }
      if (column.sortable) {
        if (column.order === OrderByEnum.ASC) {
          elm.classList.remove(OrderByEnum.DESC)
          elm.classList.add(OrderByEnum.ASC)
        } else if (column.order === OrderByEnum.DESC) {
          elm.classList.remove(OrderByEnum.ASC)
          elm.classList.add(OrderByEnum.DESC)
        } else {
          elm.classList.remove(OrderByEnum.DESC)
          elm.classList.remove(OrderByEnum.ASC)
        }
      }
    },
    sortChange ({ column, prop, order }) {
      if (column) {
        this.headerSortChange(column, this.$el.querySelector(`.${column.id}`))
      } else {
        let sortCols = this.$el.querySelectorAll('th.is-sortable')
        for (let i = 0; i < sortCols.length; i++) {
          sortCols[i].classList.remove(OrderByEnum.DESC)
          sortCols[i].classList.remove(OrderByEnum.ASC)
        }
      }
      // sort table by column, reset page to top and update table
      // if column is not defined, fall back to default sort
      if (_.isUndefined(column) || _.isNull(column)) {
        this.tableData.defaultSort()
      } else {
        this.tableData.sortBy(column.type, prop, order)
      }
      this.updateTableData(true)
      this.$emit('update-record-count')
    },
    updateTableData () {
      this.tableData.currentPage = 1
      this.setData()
      this.refreshLayout()
    },
    refreshLayout () {
      this.$refs.cTable.doLayout()
    },
    getCurrentPageRecords () {
      return this.tableData.getRecords()
    },
    changePage (pageNumber) {
      this.tableData.currentPage = pageNumber
      this.setData()
      this.$emit('update-record-count')
    },
    setData () {
      const recordsData = this.getCurrentPageRecords()
      this.collection = recordsData.records
      this.tableData.setIndexData(recordsData.startIndex, recordsData.endIndex)
    },
    copyToClipboard (row, colName) {
      const hash = this.getObjectPathValue(row, colName)
      navigator.clipboard.writeText(hash).then(
        () => {
          this.$alert({
            width: '400',
            type: 'success',
            duration: 3000,
            text: 'Successfully copied to clipboard'
          })
        },
        err => {
          this.$alert({
            width: '400',
            type: 'error',
            duration: 3000,
            text: `Error while copying to clipboard: ${err}`
          })
        }
      )
    }
  },
  beforeMount () {
    if (this.tableData.hasOwnProperty('models')) {
      this.setData()
    }
  },
  mounted () {
    if (this.heightOffset > 0) {
      this.$refs.cTable.layout.setHeight(this.$el.parentElement.clientHeight - this.heightOffset)
      let tableBodyElm = this.$el.querySelector('.el-table__body-wrapper')
      if (tableBodyElm) {
        tableBodyElm.style.height = `${this.$el.parentElement.clientHeight - this.heightOffset - 82}px`
      }
      let tableBodyElement = this.$el.querySelector('.el-table--enable-row-transition')
      if (tableBodyElement) {
        tableBodyElement.style.height = `${this.$el.parentElement.clientHeight - this.heightOffset - 19}px`
      }
    }
  },
  components: {
    StateTag,
    UserButton,
    CyberKillChainTickChart,
    RecommendationProgress,
    PercentComplete,
    IntegrationStatusTableCell,
    AssetCountTableCell,
    SensorCoverageTableCell,
    DiscoverySourceTableCell,
    InstanceEntityTableCell,
    AnomalyDismissTableCell,
    NeedsAttentionTableCell,
    SnykVulnerableTableCell,
    ContainerImageNeedsAttentionTableCell
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/colors.css';
@import 'assets/styles/tables.css';
</style>



// WEBPACK FOOTER //
// src/components/table/BasicTableElement.vue